/**
 * Browser side of company careers pages (rules and shapes in ./careersPage.ts).
 *
 *   resolveCareersSlug(x)  is /jobs/<x> a company page? Reads careerSlugs/<x> (public get). Returns
 *                          the org id and, for an old name, the company's current name to redirect to.
 *   checkCareersSlug / saveCareersPage  the callables in functions/src/careersPage.ts.
 */
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, appCheckSettled } from '../firebase';
import { ensureDb } from '../firestore/ensureDb';
import { couldBeCareersSlug, orgCareersSlug, type CareersPageSettings } from './careersPage';

export type SlugResolution =
  | { kind: 'job' }
  | { kind: 'company'; slug: string; orgId: string; org: Record<string, unknown> }
  | { kind: 'redirect'; to: string; orgId: string };

const cache = new Map<string, SlugResolution>();

async function withRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= attempts) throw err;
      await new Promise((r) => setTimeout(r, 1000 * attempt));
    }
  }
}

/**
 * What /jobs/<x> shows. Anything that is not slug-shaped is a job with no extra read. A failed read
 * throws (the caller shows "couldn't load" with Try again), it never silently becomes a job page.
 */
export async function resolveCareersSlug(x: string): Promise<SlugResolution> {
  if (!couldBeCareersSlug(x)) return { kind: 'job' };
  const hit = cache.get(x);
  if (hit) return hit;
  const db = ensureDb();
  await appCheckSettled; // a signed-out visitor's first read must not race App Check start-up
  const claim = await withRetry(() => getDoc(doc(db, 'careerSlugs', x)));
  if (!claim.exists()) {
    const r: SlugResolution = { kind: 'job' };
    cache.set(x, r);
    return r;
  }
  const data = claim.data() as { orgId?: unknown; active?: unknown };
  const orgId = typeof data.orgId === 'string' ? data.orgId : '';
  if (!orgId) return { kind: 'job' };
  const orgSnap = await withRetry(() => getDoc(doc(db, 'organizations', orgId)));
  const org = orgSnap.exists() ? (orgSnap.data() as Record<string, unknown>) : null;
  const current = orgCareersSlug(org);
  let r: SlugResolution;
  if (!org) r = { kind: 'job' };
  else if (data.active === true && current === x) r = { kind: 'company', slug: x, orgId, org };
  else if (current) r = { kind: 'redirect', to: current, orgId };
  else r = { kind: 'job' };
  if (r.kind !== 'company') cache.set(x, r);
  return r;
}

export type SlugCheck = { available: boolean; reason: 'ok' | 'format' | 'taken' | 'job'; message: string; suggestions: string[] };

function functionsApp() {
  if (!app) throw new Error('Firebase app not initialized');
  return getFunctions(app);
}

export async function checkCareersSlug(orgId: string, slug: string): Promise<SlugCheck> {
  const fn = httpsCallable<{ orgId: string; slug: string }, SlugCheck>(functionsApp(), 'checkCareersSlug');
  return (await fn({ orgId, slug })).data;
}

export type SaveCareersResult = { ok: true; slug: string; previousSlug: string | null };

export async function saveCareersPage(orgId: string, settings: CareersPageSettings, brandColor: string): Promise<SaveCareersResult> {
  const fn = httpsCallable<{ orgId: string; settings: CareersPageSettings; brandColor: string }, SaveCareersResult>(functionsApp(), 'saveCareersPage');
  return (await fn({ orgId, settings, brandColor })).data;
}

/** The problems list the callable attaches to an invalid-argument / already-exists error. */
export function callableProblems(err: unknown): { problems: Array<{ field: string; message: string }>; suggestions: string[] } {
  const details = (err as { details?: { problems?: unknown; suggestions?: unknown } } | null)?.details;
  const problems = Array.isArray(details?.problems) ? (details!.problems as Array<{ field: string; message: string }>) : [];
  const suggestions = Array.isArray(details?.suggestions) ? (details!.suggestions as string[]) : [];
  return { problems, suggestions };
}

// ─── The branding a careers visitor carries into sign-up ────────────────────

/**
 * When a visitor opens a job of a company that has its own careers page, the job page, its apply
 * page and the EMT sign-up screens after it show that company's logo, colour and "Applying to
 * <Company>". Kept in sessionStorage (this tab only) like the post-login redirect, and cleared when
 * the application is submitted or the visitor opens a job of a company without a careers page.
 */
export type CareersBrand = { orgId: string; name: string; logoURL: string; color: string; jobId: string; jobTitle: string; slug: string };
const BRAND_KEY = 'arevinaCareersBrand';

export function rememberCareersBrand(brand: CareersBrand | null): void {
  try {
    if (brand) sessionStorage.setItem(BRAND_KEY, JSON.stringify({ ...brand, t: Date.now() }));
    else sessionStorage.removeItem(BRAND_KEY);
  } catch {
    // Storage blocked: the sign-up screens show Arevina's own look, which still works.
  }
}

export function readCareersBrand(): CareersBrand | null {
  try {
    const raw = sessionStorage.getItem(BRAND_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as CareersBrand & { t?: number };
    // A day is plenty for one sign-up; an old tab never brands a later visit.
    if (!v || typeof v.orgId !== 'string' || !v.name || (v.t && Date.now() - v.t > 24 * 3600 * 1000)) return null;
    return v;
  } catch {
    return null;
  }
}
